import {DestroyRef, Directive, inject, Injectable, NgZone} from '@angular/core';
import {NgbDropdown, NgbModal, NgbOffcanvas} from '@ng-bootstrap/ng-bootstrap';
import {Observable, Subject} from 'rxjs';

declare global {
    interface Window {
        ['dismissTopOverlay']?: () => boolean;
    }
}

const hasNativeAppBridge = (): boolean => {
    if ('messageHandler' in window === false) {
        return false;
    }
    const handler: unknown = window.messageHandler;
    if (typeof handler !== 'object' || handler === null) {
        return false;
    }
    return 'postMessage' in handler && typeof handler.postMessage === 'function';
};

export type OverlayStateChange = 'OVERLAY_OPENED' | 'OVERLAY_CLOSED';

@Injectable({providedIn: 'root'})
export class OverlayStateService {
    private readonly state$ = new Subject<OverlayStateChange>();
    readonly changes$: Observable<OverlayStateChange> = this.state$.asObservable();

    notify(state: OverlayStateChange): void {
        this.state$.next(state);
    }
}

type OverlayEntry = {
    dismiss: () => void;
    removed: boolean;
};

export type OverlayHandle = {
    remove: VoidFunction;
    abandon: VoidFunction;
};

/**
 * Unified dismiss stack for all overlay types (modals, offcanvas, dropdowns).
 * A single popstate listener ensures only the topmost overlay is dismissed
 * per back-gesture, preventing cross-contamination between overlay types.
 */
@Injectable({providedIn: 'root'})
export class OverlayDismissStack {
    private readonly zone = inject(NgZone);
    private readonly overlayState = inject(OverlayStateService);
    private readonly destroyRef = inject(DestroyRef);

    private readonly stack: OverlayEntry[] = [];
    private pendingPopstateSkips = 0;

    constructor() {
        window.addEventListener('popstate', this.onPopState);
        this.destroyRef.onDestroy(() => {
            window.removeEventListener('popstate', this.onPopState);
            delete window.dismissTopOverlay;
        });

        window.dismissTopOverlay = (): boolean => {
            const entry = this.stack.pop();
            if (entry === undefined) {
                return false;
            }
            entry.removed = true;
            this.zone.run(() => entry.dismiss());
            return true;
        };
    }

    /**
     * Push an overlay onto the dismiss stack.
     * Returns a handle with two removal strategies:
     * - remove(): cleans up the synthetic history entry via history.back()
     * - abandon(): skips history.back() (use when navigation already happened)
     * Both are idempotent after first call.
     */
    push(dismiss: () => void): OverlayHandle {
        const entry: OverlayEntry = {dismiss, removed: false};
        this.stack.push(entry);

        const usesHistory = hasNativeAppBridge() === false;
        if (usesHistory) {
            history.pushState(null, '');
        }
        this.overlayState.notify('OVERLAY_OPENED');

        const detach = (): void => {
            const index = this.stack.indexOf(entry);
            if (index !== -1) {
                this.stack.splice(index, 1);
            }
            entry.removed = true;
        };

        return {
            remove: (): void => {
                if (entry.removed) {
                    this.overlayState.notify('OVERLAY_CLOSED');
                    return;
                }
                detach();
                if (usesHistory) {
                    this.pendingPopstateSkips++;
                    history.back();
                }
                this.overlayState.notify('OVERLAY_CLOSED');
            },
            abandon: (): void => {
                if (entry.removed) {
                    this.overlayState.notify('OVERLAY_CLOSED');
                    return;
                }
                detach();
                this.overlayState.notify('OVERLAY_CLOSED');
            },
        };
    }

    private onPopState = (): void => {
        if (this.pendingPopstateSkips > 0) {
            this.pendingPopstateSkips--;
            return;
        }

        const entry = this.stack.pop();
        if (entry === undefined) {
            return;
        }

        entry.removed = true;
        this.zone.run(() => entry.dismiss());
        // OVERLAY_CLOSED is sent when the overlay's close callback
        // calls handle.remove() or handle.abandon().
    };
}

@Directive({
    selector: '[appNgbOverlayDismiss]',
    standalone: true,
})
export class NgbOverlayDismissDirective {
    constructor() {
        const destroyRef = inject(DestroyRef);
        const modalService = inject(NgbModal);
        const offcanvasService = inject(NgbOffcanvas);
        const stack = inject(OverlayDismissStack);

        const originalModalOpen = modalService.open.bind(modalService);
        modalService.open = (
            ...args: Parameters<typeof originalModalOpen>
        ): ReturnType<typeof originalModalOpen> => {
            const ref = originalModalOpen(...args);
            const handle = stack.push(() => ref.dismiss());
            void ref.result.finally(() => handle.remove());
            return ref;
        };

        const originalOffcanvasOpen = offcanvasService.open.bind(offcanvasService);
        offcanvasService.open = (
            ...args: Parameters<typeof originalOffcanvasOpen>
        ): ReturnType<typeof originalOffcanvasOpen> => {
            const ref = originalOffcanvasOpen(...args);
            const handle = stack.push(() => ref.dismiss());
            void ref.result.finally(() => handle.remove());
            return ref;
        };

        const dropdownHandles = new WeakMap<NgbDropdown, OverlayHandle>();
        // eslint-disable-next-line @typescript-eslint/unbound-method -- stored to restore on destroy, always called with .call(this)
        const originalDropdownOpen = NgbDropdown.prototype.open;

        NgbDropdown.prototype.open = function (this: NgbDropdown): void {
            originalDropdownOpen.call(this);
            if (
                hasNativeAppBridge() === false ||
                this.isOpen() === false ||
                dropdownHandles.has(this)
            ) {
                return;
            }

            const urlAtOpen = location.href;
            const handle = stack.push(() => this.close());
            dropdownHandles.set(this, handle);

            const subscription = this.openChange.subscribe(isOpen => {
                if (isOpen) {
                    return;
                }
                subscription.unsubscribe();
                const storedHandle = dropdownHandles.get(this);
                if (storedHandle === undefined) {
                    return;
                }
                dropdownHandles.delete(this);
                if (location.href !== urlAtOpen) {
                    storedHandle.abandon();
                    return;
                }
                storedHandle.remove();
            });
        };

        destroyRef.onDestroy(() => {
            modalService.open = originalModalOpen;
            offcanvasService.open = originalOffcanvasOpen;
            NgbDropdown.prototype.open = originalDropdownOpen;
        });
    }
}
